/**
 * Frontend Account Service
 * 
 * Client-side service for account management operations
 */

import { 
  Account, 
  CreateAccountRequest, 
  UpdateAccountRequest, 
  CloseAccountRequest,
  AccountListParams,
  AccountListResponse,
  AccountType
} from '../types/account.types';

export interface ApiResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export class FrontendAccountService {
  private baseUrl: string;
  private authToken: string;

  constructor(baseUrl: string = '/api', authToken: string = '') {
    this.baseUrl = baseUrl;
    this.authToken = authToken;
  }

  setAuthToken(token: string): void {
    this.authToken = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        code: 'UNKNOWN_ERROR',
        message: 'An unknown error occurred'
      }));

      return {
        error: {
          code: error.code || 'HTTP_ERROR',
          message: error.message || `HTTP ${response.status}: ${response.statusText}`,
          details: error.details
        }
      };
    }

    try {
      const data = await response.json();
      return { data };
    } catch (error) {
      return {
        error: {
          code: 'PARSE_ERROR',
          message: 'Failed to parse response',
          details: error
        }
      };
    }
  }

  private async handleNetworkError<T>(fetchPromise: Promise<Response>): Promise<ApiResponse<T>> {
    try {
      const response = await fetchPromise;
      return this.handleResponse<T>(response);
    } catch (error) {
      // Handle network errors (e.g., server not running, CORS issues)
      if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server. Please make sure the API server is running on port 3000.',
            details: error
          }
        };
      }
      // Re-throw unexpected errors
      throw error;
    }
  }

  async getAccounts(params: AccountListParams = {}): Promise<ApiResponse<AccountListResponse>> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const url = `${this.baseUrl}/accounts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    return this.handleNetworkError<AccountListResponse>(
      fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      })
    );
  }

  async getAccountById(accountId: string): Promise<ApiResponse<Account>> {
    return this.handleNetworkError<Account>(
      fetch(`${this.baseUrl}/accounts/${accountId}`, {
        method: 'GET',
        headers: this.getHeaders()
      })
    );
  }

  async createAccount(accountData: CreateAccountRequest): Promise<ApiResponse<Account>> {
    return this.handleNetworkError<Account>(
      fetch(`${this.baseUrl}/accounts`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(accountData)
      })
    );
  }

  async updateAccount(accountId: string, updateData: UpdateAccountRequest): Promise<ApiResponse<Account>> {
    return this.handleNetworkError<Account>(
      fetch(`${this.baseUrl}/accounts/${accountId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updateData)
      })
    );
  }

  async closeAccount(accountId: string, closeData: CloseAccountRequest): Promise<ApiResponse<Account>> {
    return this.handleNetworkError<Account>(
      fetch(`${this.baseUrl}/accounts/${accountId}/close`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(closeData)
      })
    );
  }

  async deleteAccount(accountId: string): Promise<ApiResponse<void>> {
    const result = await this.handleNetworkError<{ success?: boolean }>(
      fetch(`${this.baseUrl}/accounts/${accountId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      })
    );

    if (result.error) {
      return result as ApiResponse<void>;
    }

    return { data: undefined };
  }

  async exportToCSV(params: AccountListParams = {}, filename?: string): Promise<ApiResponse<Blob>> {
    try {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      if (filename) {
        searchParams.append('filename', filename);
      }

      const url = `${this.baseUrl}/accounts/export/csv${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': this.authToken ? `Bearer ${this.authToken}` : '',
          'Accept': 'text/csv'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          code: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred'
        }));

        return {
          error: {
            code: error.code || 'HTTP_ERROR',
            message: error.message || `HTTP ${response.status}: ${response.statusText}`,
            details: error.details
          }
        };
      }

      const blob = await response.blob();
      return { data: blob };
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server. Please make sure the API server is running on port 3000.',
            details: error
          }
        };
      }
      throw error;
    }
  }

  async exportToExcel(params: AccountListParams = {}, filename?: string): Promise<ApiResponse<Blob>> {
    try {
      const searchParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });

      if (filename) {
        searchParams.append('filename', filename);
      }

      const url = `${this.baseUrl}/accounts/export/excel${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': this.authToken ? `Bearer ${this.authToken}` : '',
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          code: 'UNKNOWN_ERROR',
          message: 'An unknown error occurred'
        }));

        return {
          error: {
            code: error.code || 'HTTP_ERROR',
            message: error.message || `HTTP ${response.status}: ${response.statusText}`,
            details: error.details
          }
        };
      }

      const blob = await response.blob();
      return { data: blob };
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Unable to connect to the server. Please make sure the API server is running on port 3000.',
            details: error
          }
        };
      }
      throw error;
    }
  }

  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  async exportAccountsCSV(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/accounts/export/csv`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Accept': 'text/csv'
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const filename = 'accounts.csv';
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('CSV export failed:', error);
      throw error;
    }
  }

  async exportAccountsExcel(): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/accounts/export/excel`, {
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const filename = 'accounts.xlsx';
      this.downloadFile(blob, filename);
    } catch (error) {
      console.error('Excel export failed:', error);
      throw error;
    }
  }

  formatCurrency(amount: number, currency: string = 'AUD'): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-AU');
  }

  getAccountTypeDisplayName(type: AccountType): string {
    const displayNames: Record<AccountType, string> = {
      [AccountType.BANK_CASH]: 'Bank/Cash',
      [AccountType.SAVINGS]: 'Savings',
      [AccountType.CREDIT_CARD]: 'Credit Card',
      [AccountType.LOAN]: 'Loan',
      [AccountType.INVESTMENTS_SUPERANNUATION]: 'Investments/Super',
      [AccountType.ASSETS]: 'Assets',
    };

    return displayNames[type] || type;
  }
}

// Export a default instance
export const frontendAccountService = new FrontendAccountService('http://localhost:3000/api', 'test-auth-token');
